/**
 * Generate Governance Map from Seeds — Auto-generates visual policy data
 * from the actual seed question data.
 *
 * ARCHITECTURE v4.0 — Replaces handmatig onderhouden VISUAL_GOVERNANCE_MAP.json
 *
 * This module analyzes all seed questions to derive:
 * - Which families require stimulus visuals (all questions have them)
 * - Which families require support visuals (any question has them)
 * - Default visual types per family (most common type)
 * - Support showWhen behavior (derived from seed data)
 *
 * The generated map has the same structure as the old JSON file,
 * ensuring backward compatibility with visualPolicyBuilder.ts.
 *
 * @see docs/architecture/VISUAL_AID_SYSTEM.md — Visual system reference
 */

import { QUESTIONS_BY_FAMILY, TAXONOMY } from '../../content'
import type { Question } from '../../content'
import { getQuestionTypeVisualRole } from '../../content/questions/questionTypeRegistry'

// ─── Types (same structure as old VISUAL_GOVERNANCE_MAP.json) ─────────────────

export interface GeneratedStimulusEntry {
  default: string | null
  required: boolean
  alternates?: string[]
}

export interface GeneratedSupportEntry {
  default: string | null
  required: boolean
  showWhen?: string
  alternates?: string[]
}

export interface GeneratedFamilyEntry {
  module: string
  skill: string
  tier: number
  levelCode: string
  stimulus: GeneratedStimulusEntry
  support: GeneratedSupportEntry
}

export interface GeneratedVisualGovernanceMap {
  families: Record<string, GeneratedFamilyEntry>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Count occurrences of each type in an array and return the most common one.
 * Returns null if the array is empty.
 */
function mostCommonType(types: string[]): string | null {
  if (types.length === 0) return null

  const counts = new Map<string, number>()
  for (const t of types) {
    counts.set(t, (counts.get(t) ?? 0) + 1)
  }

  let maxCount = 0
  let maxType: string | null = null
  for (const [type, count] of counts) {
    if (count > maxCount) {
      maxCount = count
      maxType = type
    }
  }
  return maxType
}

/**
 * Get all unique types from an array, excluding the default.
 */
function getAlternates(types: string[], defaultType: string | null): string[] {
  const unique = Array.from(new Set(types)).filter((t) => t !== defaultType)
  return unique.length > 0 ? unique.sort() : []
}

/**
 * Extract the most common showWhen value from support aids.
 */
function mostCommonShowWhen(questions: Question[]): string | undefined {
  const showWhens: string[] = []
  for (const q of questions) {
    if (!q.supportAids) continue
    for (const aid of q.supportAids) {
      const sw = (aid as { showWhen?: string }).showWhen
      if (sw) showWhens.push(sw)
    }
  }
  if (showWhens.length === 0) return undefined
  return mostCommonType(showWhens) ?? undefined
}

// ─── Generator ────────────────────────────────────────────────────────────────

/**
 * Generate the visual governance map from actual seed data.
 *
 * This replaces the handmatig maintained VISUAL_GOVERNANCE_MAP.json.
 * It analyzes all questions per family to derive visual requirements.
 *
 * Rules for derivation:
 * - stimulus.required = true if ALL CONTEXTUAL questions in the family
 *   have stimulusAids. EMBEDDED-role questions (visualRole 'embedded' in
 *   QUESTION_TYPE_VISUAL_ROLE) are excluded from the consensus — their
 *   renderer is the visual surface, so they legitimately ship without
 *   stimulusAids. Without this exclusion a single embedded question
 *   could flip stimulus.required for a whole family from true → false.
 *   (Governance Structural Refactor 2026 — Fase 3.)
 * - stimulus.default = most common stimulus type across all questions
 *   (embedded + contextual, so any stimulus an embedded question does
 *   provide still contributes to the family's canonical default).
 * - support.required = true if ANY question has supportAids
 *   (support is generally expected when present in seeds; embedded
 *   types still benefit from support visuals during feedback).
 * - support.default = most common support type across questions
 * - support.showWhen = most common showWhen value from support aids
 */
export function generateGovernanceMapFromSeeds(): GeneratedVisualGovernanceMap {
  const families: Record<string, GeneratedFamilyEntry> = {}

  for (const familyDef of TAXONOMY) {
    const questions = QUESTIONS_BY_FAMILY.get(familyDef.familyId)
    if (!questions || questions.length === 0) continue

    const qs = questions

    // ── Filter EMBEDDED-role questions OUT of consensus calculation ────────
    // Governance Structural Refactor 2026 — Fase 3 (Auto-Policy Fix).
    //
    // Question types whose renderer IS the visual surface (visualRole
    // 'embedded' — clock, comparison_judge, ordering, place_value_build, …)
    // legitimately ship without `stimulusAids` because the renderer
    // composes the answerable visual itself. Counting them in the
    // "ALL questions have stimulus" consensus caused a single embedded
    // question to flip `stimulus.required` from true → false for the
    // whole family — the historic auto-policy-flip bug.
    //
    // We now compute consensus over CONTEXTUAL questions only. Embedded
    // questions still contribute to `stimulus.default` discovery via
    // `mostCommonType` (so their occasional stimulus aid is still
    // observed), but they do not vote on whether stimulus is required.
    const contextualQs = qs.filter(
      (q) => getQuestionTypeVisualRole(q.questionType ?? '') !== 'embedded',
    )

    // ── Analyze stimulus aids ──────────────────────────────────────────────

    const allStimulusTypes: string[] = []
    let contextualWithStimulus = 0

    for (const q of qs) {
      if (Array.isArray(q.stimulusAids) && q.stimulusAids.length > 0) {
        for (const aid of q.stimulusAids) {
          if (aid.type) allStimulusTypes.push(aid.type)
        }
      }
    }
    for (const q of contextualQs) {
      if (Array.isArray(q.stimulusAids) && q.stimulusAids.length > 0) {
        contextualWithStimulus++
      }
    }

    const stimulusDefault = mostCommonType(allStimulusTypes)
    // Required = ALL CONTEXTUAL questions have stimulus (consensus-based).
    // Families that consist entirely of embedded questions have
    // `contextualQs.length === 0` and therefore stimulus is NOT required
    // (correct: nothing external is needed when every renderer is its
    // own visual surface).
    const stimulusRequired =
      contextualQs.length > 0 && contextualWithStimulus === contextualQs.length
    const stimulusAlternates = getAlternates(allStimulusTypes, stimulusDefault)

    // ── Analyze support aids ──────────────────────────────────────────────
    // Support is treated more loosely than stimulus: ANY question with a
    // support aid signals the family uses support. We still observe ALL
    // questions (embedded + contextual) because support aids appear in
    // the feedback flow and are equally valuable for both roles.

    const allSupportTypes: string[] = []
    let questionsWithSupport = 0

    for (const q of qs) {
      if (Array.isArray(q.supportAids) && q.supportAids.length > 0) {
        questionsWithSupport++
        for (const aid of q.supportAids) {
          if (aid.type) allSupportTypes.push(aid.type)
        }
      }
    }

    const supportDefault = mostCommonType(allSupportTypes)
    // Required = ANY question has support aids (support is expected when present)
    const supportRequired = questionsWithSupport > 0
    const showWhen = mostCommonShowWhen(qs)
    const supportAlternates = getAlternates(allSupportTypes, supportDefault)

    // ── Build entry ───────────────────────────────────────────────────────

    const entry: GeneratedFamilyEntry = {
      module: familyDef.module,
      skill: familyDef.skill,
      tier: familyDef.tier,
      levelCode: familyDef.levelCode,
      stimulus: {
        default: stimulusDefault,
        required: stimulusRequired,
        ...(stimulusAlternates.length > 0
          ? { alternates: stimulusAlternates }
          : {}),
      },
      support: {
        default: supportDefault,
        required: supportRequired,
        ...(showWhen ? { showWhen } : {}),
        ...(supportAlternates.length > 0
          ? { alternates: supportAlternates }
          : {}),
      },
    }

    families[familyDef.familyId] = entry
  }

  return { families }
}

// ─── Module-level Cache (lazy singleton) ──────────────────────────────────────

let _cachedMap: GeneratedVisualGovernanceMap | null = null

/**
 * Get the generated governance map, using a module-level cache.
 * The map is built once from seed data and reused for all subsequent calls.
 * Call `invalidateGovernanceMapCache()` to force a rebuild.
 */
export function getGeneratedGovernanceMap(): GeneratedVisualGovernanceMap {
  if (!_cachedMap) {
    _cachedMap = generateGovernanceMapFromSeeds()
  }
  return _cachedMap
}

/**
 * Invalidate the cached governance map, forcing a rebuild on next access.
 */
export function invalidateGovernanceMapCache(): void {
  _cachedMap = null
}

/**
 * Get the number of families in the generated governance map.
 * Useful for diagnostics and validation.
 */
export function getGeneratedMapFamilyCount(): number {
  const map = getGeneratedGovernanceMap()
  return Object.keys(map.families).length
}
